import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  GroupStudentSeriesScores,
  GroupWithScores,
  StudentWithScores,
  UserTrajectoryScores,
  getGroupScoresByChapterMapper,
  getTrajectoryScoresByGroupMapper,
  getTrajectoryScoresByUserMapper,
  getUserScoresByChapterMapper,
} from 'src/config/api/models/Score';
import apiRoutes, { baseURL } from 'src/config/api/routes';
import { RootState } from 'src/modules/shared/store';

type getScoreParams = {
  pageId: string;
};

type getPageScoreByGroupParams = {
  pageId: string;
  groupId: string;
};

type getStudentPageScoreByGroupParams = {
  pageId: string;
  groupId: string;
  studentId: string;
};

type getGroupScoreByChapterParams = {
  bookId: string;
  groupId: string;
  chapterId: string;
};

type getStudentScoreByChapterParams = {
  bookId: string;
  groupId: string;
  chapterId: string;
  studentId: string;
};

type getUserScoreByChapterParams = {
  bookId: string;
  chapterId: string;
};

export const scoreApi = createApi({
  reducerPath: 'scoreApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.jwt;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getPageScore: builder.query<UserTrajectoryScores | undefined, getScoreParams>({
      query: ({ pageId }) => ({
        url: apiRoutes.userPageScores(pageId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getTrajectoryScoresByUserMapper(response),
    }),
    getPageScoreByGroup: builder.mutation<GroupStudentSeriesScores, getPageScoreByGroupParams>({
      query: ({ pageId, groupId }) => ({
        url: apiRoutes.groupPageScores(groupId, pageId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getTrajectoryScoresByGroupMapper(response),
    }),
    getStudenPageScoreByGroup: builder.mutation<
      UserTrajectoryScores | undefined,
      getStudentPageScoreByGroupParams
    >({
      query: ({ pageId, groupId, studentId }) => ({
        url: apiRoutes.groupStudentScores(groupId, pageId, studentId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getTrajectoryScoresByUserMapper(response),
    }),
    getGroupScoreByChapter: builder.mutation<
      GroupWithScores | undefined,
      getGroupScoreByChapterParams
    >({
      query: ({ bookId, chapterId, groupId }) => ({
        url: apiRoutes.groupScoresByChapter(groupId, bookId, chapterId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getGroupScoresByChapterMapper(response),
    }),
    getStudentScoreByChapter: builder.mutation<
      StudentWithScores | undefined,
      getStudentScoreByChapterParams
    >({
      query: ({ bookId, chapterId, groupId, studentId }) => ({
        url: apiRoutes.studentScoresByChapter(groupId, bookId, chapterId, studentId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getUserScoresByChapterMapper(response),
    }),
    getUserScoreByChapter: builder.query<
      StudentWithScores | undefined,
      getUserScoreByChapterParams
    >({
      query: ({ bookId, chapterId }) => ({
        url: apiRoutes.userScoresByChapter(bookId, chapterId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getUserScoresByChapterMapper(response),
    }),
  }),
});

export const {
  useGetPageScoreQuery,
  useGetPageScoreByGroupMutation,
  useGetStudenPageScoreByGroupMutation,
  useGetStudentScoreByChapterMutation,
  useGetUserScoreByChapterQuery,
  useGetGroupScoreByChapterMutation
} = scoreApi;
